import { useCallback, useEffect, useRef, useState } from "react";

const SPEED_ALERT_PAUSE = 15_000;

export const Severity = {
  LOW: "Low",
  MEDIUM: "Medium",
  HIGH: "High",
  CRITICAL: "Critical",
} as const;

export type SocketCuePayload = {
  type: string;
  detail: string;
  metadata: {
    display_text: string;
    threshold?: number;
    id?: string | number;
    name?: string;
    description?: string;
    word_indexes?: string;
    word?: string;
    count?: number;
  };
};

export type Cue = {
  id: string | number;
  name: string;
  description?: string;
  severity: string;
  detail?: string;
};

export const useCues = () => {
  const [triggered, setTriggered] = useState(new Array<SocketCuePayload>());
  const [pendingCues, setPendingCues] = useState<Cue[]>([]);
  const [allowCue, setAllowCue] = useState<boolean>(true);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleResetCues = useCallback(() => {
    setPendingCues([]);
    setTriggered(new Array<SocketCuePayload>());
  }, []);

  const handleAcknowledge = useCallback((cue: Cue) => {
    setPendingCues((prev) => prev.filter((c) => c.id !== cue.id));
  }, []);

  const handleCue = useCallback(
    (cueData: SocketCuePayload[]) => {
      for (const cue of cueData) {
        const cueType = cue.type;

        if (cueType === "speed" || cueType === "fillerword") {
          if (!allowCue) {
            continue;
          }

          setAllowCue(false);
          clearTimeout(timeoutRef.current ?? undefined);
          timeoutRef.current = setTimeout(() => setAllowCue(true), SPEED_ALERT_PAUSE);
        }
        const description = cue.metadata.display_text;
        const detail =
          cueType === "fillerword"
            ? `"${cue.metadata.word}" said ${cue.metadata.count} times.`
            : "";
        const severity =
          cueType === "speed"
            ? Severity.LOW
            : cueType === "fillerword"
              ? Severity.LOW
              : Severity.MEDIUM;

        const triggeredCue: Cue = {
          id: cue.metadata.id ?? `cue-${Date.now()}`,
          name: cue.metadata.name ?? "Cue",
          severity,
          description,
          detail,
        };

        setPendingCues((prev) => {
          if (prev.some((condition) => condition.id === triggeredCue.id)) {
            return prev;
          }
          return [...prev, triggeredCue];
        });
        setTriggered((prev) => [...prev, cue]);
      }
    },
    [allowCue],
  );

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        setAllowCue(true);
      }
    },
    [],
  );

  return {
    pendingCues,
    onAcknowledge: handleAcknowledge,
    triggered,
    resetCues: handleResetCues,
    onCue: handleCue,
  };
};
